import React, { useState, useEffect, useCallback } from 'react';
import {
  Container,
  Typography,
  Box,
  ImageList,
  ImageListItem,
  Checkbox,
  Button,
  Alert,
  CircularProgress,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  IconButton
} from '@mui/material';
import { Download as DownloadIcon, Visibility as VisibilityIcon, CheckCircle as CheckCircleIcon } from '@mui/icons-material';
import { useImages } from '../context/images/ImagesContext';
import { useServices } from '../context/services/ServicesContext';
import ImageUploader from '../components/common/ImageUploader';
import ProcessingModal from '../components/common/ProcessingModal';
import { toast } from 'react-toastify';

const CroppedPage = () => {
  const { croppedImages, loading, error, refreshCroppedImages, deleteImage, selectedInfluencer, fetchAllImages } = useImages();
  const { processingService } = useServices();
  const [selectedImages, setSelectedImages] = useState(new Set());
  const [captionMode, setCaptionMode] = useState(false);
  const [processingModalOpen, setProcessingModalOpen] = useState(false);
  const [processingStatus, setProcessingStatus] = useState(null);
  const [missingCaptions, setMissingCaptions] = useState([]);

  // Fetch cropped images when influencer changes
  useEffect(() => {
    if (selectedInfluencer?.id) {
      refreshCroppedImages();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedInfluencer?.id]); // Only re-run when influencer ID changes

  // Calculate missing captions
  useEffect(() => {
    if (croppedImages && croppedImages.length > 0) {
      // Check which images don't have captions yet
      const missing = croppedImages.filter(img => {
        // Check if there's a corresponding caption file
        const imageKey = img.s3Key || img.key;
        if (!imageKey) return false;
        const captionKey = imageKey.replace(/\.(jpg|jpeg|png)$/i, '.txt');
        return !croppedImages.some(i => (i.s3Key || i.key) === captionKey);
      });
      setMissingCaptions(missing);
    }
  }, [croppedImages]);

  const handleSelectAll = () => {
    if (selectedImages.size === croppedImages?.length) {
      setSelectedImages(new Set());
    } else {
      setSelectedImages(new Set(croppedImages?.map((_, index) => index) || []));
    }
  };

  const handleImageSelect = (index) => {
    const newSelected = new Set(selectedImages);
    if (newSelected.has(index)) {
      newSelected.delete(index);
    } else {
      newSelected.add(index);
    }
    setSelectedImages(newSelected);
  };

  const handleCaptionSelected = async () => {
    if (selectedImages.size === 0) {
      toast.warning('Please select images to caption');
      return;
    }

    const selectedImageData = Array.from(selectedImages).map(index => ({
      key: croppedImages[index].key,
      filename: croppedImages[index].key.split('/').pop()
    }));

    try {
      setProcessingModalOpen(true);
      setProcessingStatus({ status: 'starting', message: 'Starting captioning job...' });

      const result = await processingService.captionImages(
        selectedInfluencer.id,
        selectedImageData,
        (progress) => {
          setProcessingStatus(progress);
        }
      );

      if (result.success) {
        toast.success(`Started captioning ${selectedImageData.length} images`);
        setProcessingStatus({ 
          status: 'completed', 
          message: `Successfully started captioning ${selectedImageData.length} images` 
        });
        
        // Clear selection
        setSelectedImages(new Set());
        setCaptionMode(false);
        
        // Refresh images after a delay
        setTimeout(() => {
          refreshCroppedImages();
        }, 3000);
      }
    } catch (error) {
      console.error('Captioning failed:', error);
      toast.error(`Captioning failed: ${error.message}`);
      setProcessingStatus({ 
        status: 'error', 
        message: `Captioning failed: ${error.message}` 
      });
    }
  };

  const handleCaptionAll = async () => {
    try {
      setProcessingModalOpen(true);
      setProcessingStatus({ status: 'starting', message: 'Starting captioning job for all cropped images...' });

      // Caption all cropped images for this influencer
      const result = await processingService.captionImages(
        selectedInfluencer.id,
        null, // null means caption all
        (progress) => {
          setProcessingStatus(progress);
        }
      );

      if (result.success) {
        toast.success('Started captioning all cropped images');
        setProcessingStatus({ 
          status: 'completed', 
          message: 'Successfully started captioning all cropped images' 
        });
        
        // Refresh images after a delay
        setTimeout(() => {
          refreshCroppedImages();
        }, 3000);
      }
    } catch (error) {
      console.error('Captioning failed:', error);
      toast.error(`Captioning failed: ${error.message}`);
      setProcessingStatus({ 
        status: 'error', 
        message: `Captioning failed: ${error.message}` 
      });
    }
  };

  const handleCaptionMissing = async () => {
    if (missingCaptions.length === 0) {
      toast.info('All images already have captions');
      return;
    }

    try {
      setProcessingModalOpen(true);
      setProcessingStatus({ 
        status: 'starting', 
        message: `Starting captioning for ${missingCaptions.length} images without captions...` 
      });

      const result = await processingService.captionImages(
        selectedInfluencer.id,
        missingCaptions.map(img => ({
          key: img.key,
          filename: img.key.split('/').pop()
        })),
        (progress) => {
          setProcessingStatus(progress);
        }
      );

      if (result.success) {
        toast.success(`Started captioning ${missingCaptions.length} images`);
        setProcessingStatus({ 
          status: 'completed', 
          message: `Successfully started captioning ${missingCaptions.length} images` 
        });
        
        // Refresh images after a delay
        setTimeout(() => {
          refreshCroppedImages();
        }, 3000);
      }
    } catch (error) {
      console.error('Captioning failed:', error);
      toast.error(`Captioning failed: ${error.message}`);
      setProcessingStatus({ 
        status: 'error', 
        message: `Captioning failed: ${error.message}` 
      });
    }
  };

  const handleDeleteSelected = async () => {
    if (!window.confirm(`Delete ${selectedImages.size} selected images?`)) {
      return;
    }

    const deletePromises = Array.from(selectedImages).map(index =>
      deleteImage(croppedImages[index].key)
    );

    try {
      await Promise.all(deletePromises);
      toast.success(`Deleted ${selectedImages.size} images`);
      setSelectedImages(new Set());
      refreshCroppedImages();
    } catch (error) {
      toast.error(`Error deleting images: ${error.message}`);
    }
  };

  if (!selectedInfluencer) {
    return (
      <Container>
        <Alert severity="info">Please select an influencer to view cropped images</Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl">
      <Box sx={{ mb: 3 }}>
        <Typography variant="h5" gutterBottom>
          Cropped Images - {selectedInfluencer.name}
        </Typography>
        <Typography variant="body2" color="text.secondary" gutterBottom>
          {croppedImages?.length || 0} cropped images | {missingCaptions.length} missing captions
        </Typography>
      </Box>

      {/* Action Buttons */}
      <Box sx={{ mb: 3, display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
        <Button
          variant="contained"
          color="primary"
          onClick={() => setCaptionMode(!captionMode)}
          startIcon={<CheckCircleIcon />}
        >
          {captionMode ? 'Cancel Selection' : 'Select for Captioning'}
        </Button>

        {captionMode && (
          <>
            <Button
              variant="outlined"
              onClick={handleSelectAll}
            >
              {selectedImages.size === croppedImages?.length ? 'Deselect All' : 'Select All'}
            </Button>
            <Button
              variant="contained"
              color="success"
              onClick={handleCaptionSelected}
              disabled={selectedImages.size === 0}
            >
              Caption {selectedImages.size} Selected
            </Button>
          </>
        )}

        <Button
          variant="contained"
          color="secondary"
          onClick={handleCaptionMissing}
          disabled={missingCaptions.length === 0}
        >
          Caption {missingCaptions.length} Missing
        </Button>

        <Button
          variant="outlined"
          color="info"
          onClick={handleCaptionAll}
        >
          Caption All
        </Button>

        {selectedImages.size > 0 && (
          <Button
            variant="outlined"
            color="error"
            onClick={handleDeleteSelected}
          >
            Delete {selectedImages.size} Selected
          </Button>
        )}
      </Box>

      {/* Upload Section */}
      <Box sx={{ mb: 3 }}>
        <ImageUploader
          influencerId={selectedInfluencer.id}
          folder="cropped"
          onUploadComplete={() => refreshCroppedImages()}
        />
      </Box>

      {/* Images Grid */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      ) : error ? (
        <Alert severity="error">{error}</Alert>
      ) : croppedImages && croppedImages.length > 0 ? (
        <ImageList variant="masonry" cols={4} gap={8}>
          {croppedImages.map((image, index) => (
            <ImageListItem key={image.key}>
              <Box sx={{ position: 'relative' }}>
                {captionMode && (
                  <Checkbox
                    sx={{ position: 'absolute', top: 0, left: 0, zIndex: 1, bgcolor: 'white' }}
                    checked={selectedImages.has(index)}
                    onChange={() => handleImageSelect(index)}
                  />
                )}
                <img
                  src={image.url}
                  alt={image.key}
                  loading="lazy"
                  style={{
                    borderRadius: '4px',
                    width: '100%',
                    cursor: captionMode ? 'pointer' : 'default',
                    opacity: captionMode && !selectedImages.has(index) ? 0.6 : 1
                  }}
                  onClick={() => captionMode && handleImageSelect(index)}
                />
                <Box sx={{ position: 'absolute', bottom: 0, right: 0, p: 0.5 }}>
                </Box>
                {missingCaptions.some(img => img.key === image.key) && (
                  <Chip
                    label="No Caption"
                    color="warning"
                    size="small"
                    sx={{ position: 'absolute', top: 8, right: 8 }}
                  />
                )}
              </Box>
            </ImageListItem>
          ))}
        </ImageList>
      ) : (
        <Alert severity="info">No cropped images found. Crop images from the Raw Dataset tab.</Alert>
      )}

      {/* Processing Modal */}
      <ProcessingModal
        open={processingModalOpen}
        onClose={() => setProcessingModalOpen(false)}
        status={processingStatus}
        title="Captioning Images"
      />
    </Container>
  );
};

export default CroppedPage;